import { Modal } from 'antd';
import { BaseButton } from '../UI/BaseButton';
import Task from '../../types/Task';
import { TaskTypeLabel } from '../UI/Dropdown/TaskTypeDropdown';
import { TaskType } from '../../types/TaskType';
import { useState, useEffect } from 'react';
import { SelectableCard } from '../Cards/SelectableCard';

interface DeleteTaskModalProps {
  task?: Task;
  subtasks?: Task[];
  open: boolean;
  onDelete: (deleteSubtasks: boolean) => void;
  onCancel: () => void;
}

enum DeleteOption {
  OnlyItem = 'only_item',
  ItemAndSubtasks = 'item_and_subtasks',
}

export const DeleteTaskModal = (props: DeleteTaskModalProps) => {
  const { open, onCancel, onDelete, task, subtasks } = props;
  const [selectedOption, setSelectedOption] = useState<DeleteOption>(DeleteOption.OnlyItem);
  const itemType = task?.type === TaskType.epic ? 'Epic' : 'Item';
  const itemsType = task?.type === TaskType.epic ? 'Items and Subitems' : 'Subitems';
  const tasksToDelete = (
    selectedOption === DeleteOption.ItemAndSubtasks && subtasks && subtasks.length > 0 ? [task, ...subtasks] : [task]
  ).filter((task): task is Task => task != null);
  const hasSubtasks = subtasks && subtasks.length > 0;

  // Reset to default option when modal opens
  useEffect(() => {
    if (open) {
      setSelectedOption(DeleteOption.OnlyItem);
    }
  }, [open]);

  return (
    <Modal
      title={
        <div className="text-brand-grey-dark text-base font-semibold mx-4 mt-3">
          You are about to delete the following{' '}
          {selectedOption === DeleteOption.ItemAndSubtasks ? `${itemType}, ${itemsType}` : itemType}:
        </div>
      }
      open={open}
      onOk={() => onDelete(selectedOption === DeleteOption.ItemAndSubtasks)}
      onCancel={onCancel}
      centered
      width={640}
      footer={
        <div className="flex justify-end gap-2">
          <BaseButton type="secondary" size="medium" onClick={onCancel}>
            Cancel
          </BaseButton>
          <BaseButton
            type="primary"
            size="medium"
            danger
            onClick={() => onDelete(selectedOption === DeleteOption.ItemAndSubtasks)}
          >
            Delete {selectedOption === DeleteOption.ItemAndSubtasks ? `${itemType}, ${itemsType}` : itemType}
          </BaseButton>
        </div>
      }
    >
      <div className="max-h-[306px] overflow-y-auto mx-4 mb-10 mt-6 p-3 bg-neutral-200 rounded flex flex-col gap-2 items-center justify-start text-brand-grey-dark text-sm font-medium ">
        {tasksToDelete &&
          tasksToDelete.map((task) => (
            <div key={task?.id} className="w-full flex items-center truncate overflow-hidden shrink-0 cursor-default">
              <TaskTypeLabel key={task?.id} type={task?.type as TaskType} size="small" />
              {task?.title}
            </div>
          ))}
      </div>
      <div className="mx-4 mb-6 text-neutral-900 text-sm font-normal">
        When you delete the {selectedOption === DeleteOption.ItemAndSubtasks ? `${itemType}, ${itemsType}` : itemType},{' '}
        it will no longer be visible in the backlog or sprint. Are you sure you want to delete it?
      </div>

      {task?.type === TaskType.epic && (
        <div className="flex gap-2 mx-4 mb-6">
          <SelectableCard
            id={DeleteOption.OnlyItem}
            title={`${itemType} Only`}
            subtitle={`Deleting selected ${itemType} only, while keeping all their Items with Subitems.`}
            selected={selectedOption === DeleteOption.OnlyItem}
            onSelected={() => setSelectedOption(DeleteOption.OnlyItem)}
          />
          <SelectableCard
            id={DeleteOption.ItemAndSubtasks}
            title={`${itemType} & its Items and Subitems`}
            subtitle={`Deleting selected ${itemType} as well as all their Items with Subitems.`}
            selected={selectedOption === DeleteOption.ItemAndSubtasks}
            onSelected={() => setSelectedOption(DeleteOption.ItemAndSubtasks)}
            disabled={!hasSubtasks}
            disabledTooltip={!hasSubtasks ? `There aren’t any Items of your selected Epic.` : ''}
          />
        </div>
      )}
      {task?.type !== TaskType.epic && task?.parentTaskId === null && (
        <div className="flex gap-2 mx-4 mb-6">
          <SelectableCard
            id={DeleteOption.OnlyItem}
            title={`${itemType} Only`}
            subtitle={`Deleting selected ${itemType} only, while keeping all its Subitems.`}
            selected={selectedOption === DeleteOption.OnlyItem}
            onSelected={() => setSelectedOption(DeleteOption.OnlyItem)}
          />
          <SelectableCard
            id={DeleteOption.ItemAndSubtasks}
            title={`${itemType} & its Subitems`}
            subtitle={`Deleting selected ${itemType} as well as all its Subitems.`}
            selected={selectedOption === DeleteOption.ItemAndSubtasks}
            onSelected={() => setSelectedOption(DeleteOption.ItemAndSubtasks)}
            disabled={!hasSubtasks}
            disabledTooltip={!hasSubtasks ? `There aren’t any Subitems of your selected Item.` : ''}
          />
        </div>
      )}
    </Modal>
  );
};
